import type React from 'react';
import { useState, useCallback, type ReactNode } from 'react';
import styled, { css, useTheme } from 'styled-components';

import Typography from 'app/ui/components/atoms/typography';
import ExpandableText from 'app/ui/components/molecules/expandable-text';

import { MinusIcon, PlusIcon } from 'app/ui/components/atoms/plus-minus';

export interface AccordionItemProps {
  title: string;
  titleExtraContent?: ReactNode;
  content?: string | ReactNode | null;
  id: string;
  additionalInfo?: string;
  contentItems?: ReactNode;
  notes?: string | null;
  expandTextByDefault?: boolean;
}
export interface AccordionItemComponentProps {
  title: string;
  titleExtraContent?: ReactNode;
  content?: string | ReactNode | null;
  id: string;
  isOpen: boolean;
  additionalInfo?: string;
  onClick?: (key: string | null) => void;
  mapPropToChildren?: any[];
  contentItems?: ReactNode;
  notes?: string | null;
  expandTextByDefault?: boolean;
  hideIcons?: boolean;
  textExpansionEnabled?: boolean;
}

export type AccordionProps = {
  data: AccordionItemProps[];
  className?: string;
  onClickEmit?: (key: string | null) => void;
  initialSelectedKey?: string;
  initialExpandedKeys?: string[];
  hideIcons?: boolean;
  textExpansionEnabled?: boolean;
};

const AccordionSection = styled.div`
  display: flex;
  flex-direction: column;
  margin: ${({ theme: { spacing } }) => `${spacing(2)} 0`};
`;

export const AccordionItem = styled.div`
  ${({ theme: { colors } }) => `
    background: ${colors.black};
    color: ${colors.white};
  `}
`;

type AccordionTitleTextColors = {
  focused?: string;
  notFocused?: string;
};

export const AccordionTitle = styled.div<{ $isOpen: boolean; index?: number; $titleColors?: AccordionTitleTextColors }>`
  display: flex;
  justify-content: space-between;
  align-items: center;
  background: transparent;
  cursor: pointer;
  padding: ${({ theme: { spacing } }) => `${spacing(2.5)} ${spacing(3)}`};
  border-top: 1px solid ${({ $isOpen, theme: { colors } }) => ($isOpen ? colors.focoltone : colors.white)};
  border-bottom: 1px solid ${({ $isOpen, theme: { colors } }) => ($isOpen ? colors.focoltone : colors.white)};

  h6 {
    color: ${({ $isOpen, theme: { colors }, $titleColors }) =>
      $isOpen ? $titleColors?.focused || colors.focoltone : $titleColors?.notFocused || colors.white};
    text-transform: uppercase;
    font-weight: 600;
    font-size: 1.25rem;
  }
`;

export const AdditionalInfoSection = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  text-align: center;
  gap: 1rem;
  flex-shrink: 0;
  padding-left: 0.5rem;
`;

export const AccordionContent = styled.div<{ $isOpen: boolean }>`
  display: ${({ $isOpen }) => ($isOpen ? 'block' : 'none')};
  padding: ${({ theme: { spacing } }) => `${spacing(4)} 0`};
`;

export const AccordionDescription = styled.div`
  margin-bottom: ${({ theme: { spacing } }) => `${spacing(4)}`};
  display: flex;
  flex-direction: column;
`;

const Icons = styled.div`
  ${({ theme: { spacing } }) => `
    width: ${spacing(6)};
    height: ${spacing(6)};

    svg {
      width: ${spacing(6)};
      height: ${spacing(6)};
    }
  `}
`;

const TitleWithContent = styled.div<{ $isOpen: boolean }>`
  display: flex;
  flex-direction: row;
  justify-content: flex-start;
  align-items: center;

  gap: ${({ theme: { spacing } }) => `${spacing(4)}`};

  ${({ $isOpen, theme: { colors } }) =>
    !$isOpen &&
    `svg {
      color: ${colors.white};
    }`};

  ${({ $isOpen, theme: { colors } }) =>
    $isOpen &&
    `svg {
      color: ${colors.focoltone};
    }`};

  ${({ theme: { media } }) => `${media.medium(
    () => `
      max-width: 80%;
    `,
  )}
  `};
`;

const ExpandableTextWrapper = styled.div`
  margin-top: ${({ theme }) => `${theme.spacing(4)}`};
`;

const EndContentWithIcons = styled.div`
  display: flex;
  flex-direction: row;
  gap: ${({ theme: { spacing } }) => `${spacing(2)}`};
  margin-left: ${({ theme: { spacing } }) => `${spacing(2)}`};
`;

const AccordionItemComponent: React.FC<AccordionItemComponentProps> = ({
  title,
  titleExtraContent,
  content,
  additionalInfo,
  id,
  isOpen,
  onClick,
  contentItems,
  notes,
  expandTextByDefault = false,
  hideIcons = false,
  textExpansionEnabled = true,
}: AccordionItemComponentProps) => {
  const theme = useTheme();

  const toggleAccordion = () => {
    if (onClick) {
      if (isOpen) {
        onClick(null);
      } else {
        onClick(id);
      }
    }
  };

  return (
    <AccordionItem id={id} data-testid="accordion-item">
      <AccordionTitle $isOpen={isOpen} onClick={() => toggleAccordion()}>
        <TitleWithContent $isOpen={isOpen}>
          <Typography variant="h6">{title}</Typography>
          {titleExtraContent}
        </TitleWithContent>
        <EndContentWithIcons>
          <AdditionalInfoSection>
            <Typography variant="h6" color={isOpen ? theme.colors.black : theme.colors.lightGray}>
              {additionalInfo}
            </Typography>
          </AdditionalInfoSection>
          {!hideIcons && (
            <Icons>
              {isOpen ? (
                <MinusIcon color={isOpen ? theme.colors.focoltone : theme.colors.white} />
              ) : (
                <PlusIcon color={isOpen ? theme.colors.focoltone : theme.colors.white} />
              )}
            </Icons>
          )}
        </EndContentWithIcons>
      </AccordionTitle>
      <AccordionContent $isOpen={isOpen}>
        {content && (
          <AccordionDescription>
            <Typography variant="body2" color={theme.colors.paragraphGray}>
              {content}
            </Typography>
          </AccordionDescription>
        )}
        {contentItems}
        {notes && (
          <ExpandableTextWrapper>
            <ExpandableText
              text={notes}
              defaultExpanded={expandTextByDefault}
              expansionEnabled={textExpansionEnabled}
            />
          </ExpandableTextWrapper>
        )}
      </AccordionContent>
    </AccordionItem>
  );
};

const Accordion: React.FC<AccordionProps> = ({
  data,
  className,
  onClickEmit,
  initialSelectedKey,
  initialExpandedKeys,
  hideIcons = false,
  textExpansionEnabled = true,
}) => {
  const [focusedKey, setFocusedKey] = useState<string | null>(initialSelectedKey || null);
  const [expandedKeys, setExpandedKeys] = useState<Set<string>>(new Set(initialExpandedKeys || []));

  const onClick = useCallback(
    (id: string | null) => {
      if (onClickEmit) {
        onClickEmit(id);
      }
      setFocusedKey(id);
    },
    [onClickEmit],
  );

  const handleToggle = useCallback(
    (id: string | null) => {
      if (!id) {
        return;
      }
      setExpandedKeys((prev) => {
        const next = new Set(prev);
        if (next.has(id)) {
          next.delete(id);
          if (onClickEmit) {
            onClickEmit(null);
          }
        } else {
          next.add(id);
          if (onClickEmit) {
            onClickEmit(id);
          }
        }
        return next;
      });
    },
    [onClickEmit],
  );

  return (
    <AccordionSection className={className}>
      {data.map((item) => {
        const isOpen = initialExpandedKeys ? expandedKeys.has(item.id) : focusedKey === item.id;
        return (
          <AccordionItemComponent
            key={item.id}
            title={item.title}
            titleExtraContent={item.titleExtraContent}
            content={item.content}
            contentItems={item.contentItems}
            additionalInfo={item.additionalInfo}
            id={item.id}
            onClick={initialExpandedKeys ? handleToggle : onClick}
            isOpen={isOpen}
            notes={item.notes}
            expandTextByDefault={item.expandTextByDefault}
            hideIcons={hideIcons}
            textExpansionEnabled={textExpansionEnabled}
          />
        );
      })}
    </AccordionSection>
  );
};

export default Accordion;
